/**
 * `#intelHeadlines` while one unit is focused (`#intelFocus`, or a map click) — client
 * round 3 Oct 2026.
 *
 * The programme headlines answer "how is the programme doing"; a reader who has just
 * focused one watershed is asking about that watershed, and leaving four programme
 * figures under its name is the panel answering a question nobody asked. So the same
 * block says the focused unit's own three numbers instead: its priority class, how many
 * of the five constraints it carries, and its agricultural area when the run measured one.
 *
 * The way back is a small "Programme" link rather than a second select: the focus itself
 * is the `#intelFocus` control a scroll above, and this is only its undo.
 *
 * Read off the units row the choropleth is already painted from, plus the one detail the
 * focus has already fetched — no request of its own.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUnitDetail, IntelUnitRow } from '../../lib/api';
import { fmtCompactNum, fmtNum } from '../../lib/format';
import { StatTile, StatTiles } from './charts/StatTile';
import { REFERENCE_YEAR } from './uc1Constants';

export interface Uc1FocusTilesProps {
  /** The focused unit's own row, or `undefined` while the rows have not landed. */
  row: IntelUnitRow | undefined;
  /** `{uc}/units/{id}`, for the agricultural area; `null` until the click's one request lands. */
  detail: IntelUnitDetail | null;
  /** The unit's name, from whichever of the two arrived first. */
  name: string;
  /**
   * `intel.uc1Chip` — `'priority'`, or one of the five constraints.
   *
   * On a constraint the tiles answer *that* question about this unit (its own flag and
   * its own measured value) rather than repeating the five-constraint count, which is the
   * Priority chip's summary and says nothing about the chip the reader is on.
   */
  chip?: string;
  onBack: () => void;
}

/** `values.ag_ha` — the hectares of cropland the run measured in this unit, when it did. */
function agHaOf(detail: IntelUnitDetail | null): number | null {
  const value = (detail?.values as Record<string, unknown> | undefined)?.ag_ha;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function Uc1FocusTiles({ row, detail, name, chip = 'priority', onBack }: Uc1FocusTilesProps) {
  const { t } = useTranslation();
  const category = row?.category ?? detail?.category ?? null;
  const flags = row?.flags ?? {};
  const nFlagged = Object.values(flags).filter((flag) => flag === true).length;
  // Only an actual `flags` block is a count: a row from a run that serves none would
  // otherwise print a confident zero for five constraints nobody looked at.
  const counted = row?.flags != null;
  const agHa = agHaOf(detail);

  const onConstraint = chip !== 'priority';
  const dimensionLabel = t(`intel.uc1.dimension.${chip}`, { defaultValue: chip });
  // `true` / `false` / `null` — flagged, not flagged, not assessed. `undefined` (a run
  // that serves no `flags`) is the same "we did not look" as `null`.
  const flag = row?.flags?.[chip] ?? null;
  const metric = row?.metrics?.[chip];
  const metricLabel = metric?.metric
    ? t(`intel.uc1.metric.${metric.metric}`, { defaultValue: metric.metric })
    : dimensionLabel;

  return (
    <div id="intelHeadlines" className="intel-headlines intel-card" data-testid="uc1-focus-tiles">
      <h5 className="intel-card-title">{name}</h5>
      <StatTiles>
        <StatTile
          id="uc1-focus-class"
          value={category ? t(`intel.uc1.category.${category}`, { defaultValue: category }) : '—'}
          text
          label={t('intel.uc1.tile.unitClass')}
        />
        {onConstraint ? (
          <>
            <StatTile
              id="uc1-focus-flag"
              value={t(
                flag === true
                  ? 'intel.uc1.legend.flagged'
                  : flag === false
                    ? 'intel.uc1.legend.notFlagged'
                    : 'intel.uc1.legend.notAssessedPlain',
              )}
              text
              label={dimensionLabel}
            />
            <StatTile
              id="uc1-focus-metric"
              value={typeof metric?.value === 'number' ? fmtNum(metric.value, 1) : '—'}
              unit={metric?.unit ?? undefined}
              label={metricLabel}
            />
          </>
        ) : (
          <StatTile
            id="uc1-focus-flags"
            value={counted ? fmtNum(nFlagged, 0) : '—'}
            label={t('intel.uc1.tile.unitFlags')}
          />
        )}
        {agHa != null ? (
          <StatTile
            id="uc1-focus-area"
            value={fmtCompactNum(agHa)}
            unit="ha"
            label={t('intel.uc1.tile.unitAgArea')}
          />
        ) : null}
        <StatTile id="uc1-focus-period" value={String(REFERENCE_YEAR)} label={t('intel.uc1.tile.period')} />
      </StatTiles>
      <button type="button" className="intel-focusback" data-testid="uc1-focus-back" onClick={onBack}>
        {t('intel.focusProgramme')}
      </button>
    </div>
  );
}

export default Uc1FocusTiles;
